import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { NuqsAdapter } from "nuqs/adapters/react-router";
import { useAuthStore } from "@groot/shell/store/auth";
import { ProtectedRoute } from "@groot/shell/components/ProtectedRoute";
import { Layout } from "@groot/shell/components/Layout";
import { ShellProvider, type ShellConfig } from "@groot/shell/lib/shell-config";
import { GrootUIProvider } from "@groot/ui";
import { Login } from "@/pages/auth/Login";
import { AccountScopedLayout } from "@/app/layout/AccountScopedLayout";
import { getSidebarNavItems, getUserMenuItems, getCommandGroups } from "@/config/navigation";
import { Dashboard } from "@/pages/dashboard/Dashboard";
import { Transactions } from "@/pages/transactions/Transactions";
import { DuplicateTransactions } from "@/pages/duplicates/DuplicateTransactions";
import { Budgets } from "@/pages/budgets/Budgets";
import { Bills } from "@/pages/bills/Bills";
import { CreditCards } from "@/pages/credit-cards/CreditCards";
import { BillForm } from "@/pages/bills/BillForm";
import { BillDetail } from "@/pages/bills/BillDetail";
import { Settings } from "@/pages/settings/Settings";
import { Jobs, JobDetail, JobsProvider } from "@groot/jobs/client";
import { Accounts } from "@/pages/accounts/Accounts";
import { AccountForm } from "@/pages/accounts/AccountForm";
import { AccountDetail } from "@/pages/accounts/AccountDetail";
import { AccountStatements } from "@/pages/accounts/AccountStatements";
import { TransactionalSms } from "@/pages/transactional-sms/TransactionalSms";
import { TransactionalSmsDetail } from "@/pages/transactional-sms/TransactionalSmsDetail";
import { MobileNotifications } from "@/pages/mobile-notifications/MobileNotifications";
import { MobileNotificationDetail } from "@/pages/mobile-notifications/MobileNotificationDetail";
import { InboundEmails } from "@/pages/inbound-emails/InboundEmails";
import { InboundEmailDetail } from "@/pages/inbound-emails/InboundEmailDetail";
import Notes from "@/pages/notes/Notes";
import Storage from "@/pages/storage/Storage";
import Bookmarks from "@/pages/bookmarks/Bookmarks";
import { StatementImport } from "@/pages/statements/StatementImport";
import { StatementAnnotate } from "@/pages/statements/StatementAnnotate";
import { StatementDetail } from "@/pages/statements/StatementDetail";
import { LoanCalculator } from "@/pages/loan-calculator/LoanCalculator";
import Payslips from "@/pages/payslips/Payslips";
import PayslipView from "@/pages/payslips/PayslipView";
import Receipts from "@/pages/receipts/Receipts";
import ReceiptView from "@/pages/receipts/ReceiptView";
import CreditReports from "@/pages/credit-reports/CreditReports";
import CreditReportView from "@/pages/credit-reports/CreditReportView";
import Workflows from "@/pages/workflows/Workflows";
import type { LinkResolver } from "@groot/jobs/client";

// App identity for the kit shell (sidebar + mobile top bar brand).
const shellConfig: ShellConfig = {
  brand: { label: "Nesin's Finance" },
};

const jobLinkResolver: LinkResolver = (key, value) => {
  if (value == null || value === "") return null;
  switch (key) {
    case "accountId":
      return { to: `/accounts/${value}`, label: String(value) };
    case "statementId":
      return { to: `/statements/${value}`, label: String(value) };
    case "fileS3Path":
      return { to: `/storage?path=${encodeURIComponent(String(value))}`, label: String(value) };
    default:
      return null;
  }
};

function App() {
  return (
    <ShellProvider config={shellConfig}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ShellProvider>
  );
}

function AppRoutes() {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const logout = useAuthStore((state) => state.logout);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasCheckedAuth = useAuthStore((state) => state.hasCheckedAuth);
  const location = useLocation();
  const navigate = useNavigate();
  // Pages with specialized full-height layouts that don't need container padding
  const isFullHeightPage =
    location.pathname === "/transactions" ||
    location.pathname === "/budgets" ||
    /^\/accounts\/\d+/.test(location.pathname);

  useEffect(() => {
    // Check auth status on mount
    checkAuth();
  }, [checkAuth]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <NuqsAdapter>
      <GrootUIProvider>
        <JobsProvider linkResolver={jobLinkResolver}>
          <Routes>
            <Route
              path="/login"
              element={hasCheckedAuth && isAuthenticated ? <Navigate to="/" replace /> : <Login />}
            />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout
                    navItems={getSidebarNavItems()}
                    userMenuItems={getUserMenuItems(handleLogout)}
                    commandGroups={getCommandGroups(navigate, handleLogout)}
                    padded={!isFullHeightPage}
                    mainClassName={isFullHeightPage ? "flex flex-col" : undefined}
                  />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              {/* Register pages get the Actual-style account picker sidebar */}
              <Route element={<AccountScopedLayout />}>
                <Route path="transactions" element={<Transactions />} />
                <Route path="accounts/:id" element={<AccountDetail />} />
              </Route>
              <Route path="duplicates" element={<DuplicateTransactions />} />
              <Route path="budgets" element={<Budgets />} />
              <Route path="credit-cards" element={<CreditCards />} />
              <Route path="bills">
                <Route index element={<Bills />} />
                <Route path="new" element={<BillForm />} />
                <Route path=":id" element={<BillDetail />} />
                <Route path=":id/edit" element={<BillForm />} />
              </Route>
              <Route path="loan-calculator" element={<LoanCalculator />} />
              <Route path="notes" element={<Notes />} />
              <Route path="bookmarks" element={<Bookmarks />} />
              <Route path="settings" element={<Settings />} />
              <Route path="accounts">
                <Route index element={<Accounts />} />
                <Route path="new" element={<AccountForm />} />
                <Route path=":id/edit" element={<AccountForm />} />
                <Route path=":id/statements" element={<AccountStatements />} />
              </Route>
              <Route path="jobs">
                <Route index element={<Jobs />} />
                <Route path=":queueName/:jobId" element={<JobDetail />} />
              </Route>
              <Route path="transactional-sms">
                <Route index element={<TransactionalSms />} />
                <Route path=":id" element={<TransactionalSmsDetail />} />
              </Route>
              <Route path="mobile-notifications">
                <Route index element={<MobileNotifications />} />
                <Route path=":id" element={<MobileNotificationDetail />} />
              </Route>
              <Route path="inbound-emails">
                <Route index element={<InboundEmails />} />
                <Route path=":id" element={<InboundEmailDetail />} />
              </Route>
              <Route path="storage" element={<Storage />} />
              <Route path="statements">
                <Route index element={<StatementImport />} />
                <Route path=":id/annotate" element={<StatementAnnotate />} />
                <Route path=":id" element={<StatementDetail />} />
              </Route>
              <Route path="payslips">
                <Route index element={<Payslips />} />
                <Route path=":id" element={<PayslipView />} />
              </Route>
              <Route path="receipts">
                <Route index element={<Receipts />} />
                <Route path=":id" element={<ReceiptView />} />
              </Route>
              <Route path="credit-reports">
                <Route index element={<CreditReports />} />
                <Route path=":id" element={<CreditReportView />} />
              </Route>
              <Route path="workflows" element={<Workflows />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </JobsProvider>
      </GrootUIProvider>
    </NuqsAdapter>
  );
}

export default App;
